import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
  LineChart, Line, ReferenceLine,
} from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, PieChart as PieIcon, BarChart2, Target, Download, FileText, Printer, Heart } from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
import { loadMoodCheckins } from '../lib/userData';
import { MOOD_OPTIONS } from './MoodCheckin';

// ── Export helpers ────────────────────────────────────────────────────────────
function downloadCSV(filename, rows) {
  const content = rows.map(r => r.map(cell =>
    typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
  ).join(',')).join('\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function exportBudgetCSV(budgetData) {
  const rows = [
    ['Finovo — Budget Export', new Date().toLocaleDateString()],
    ['Monthly Income', budgetData?.income || 0],
    [''],
    ['Category', 'Budgeted', 'Spent', 'Remaining', '% Used'],
    ...(budgetData?.categories || []).map(c => [
      c.name,
      c.budgeted || 0,
      c.spent || 0,
      (c.budgeted || 0) - (c.spent || 0),
      c.budgeted > 0 ? `${Math.round(((c.spent || 0) / c.budgeted) * 100)}%` : '—',
    ]),
  ];
  downloadCSV(`finovo-budget-${new Date().toISOString().slice(0,10)}.csv`, rows);
}

function exportNetWorthCSV(netWorthData) {
  const assets = netWorthData?.assets || [];
  const liabs  = netWorthData?.liabilities || [];
  const history = netWorthData?.history || [];
  const rows = [
    ['Finovo — Net Worth Export', new Date().toLocaleDateString()],
    [''],
    ['ASSETS', 'Value', 'Type'],
    ...assets.map(a => [a.name, a.value || 0, a.type || '']),
    ['Total Assets', assets.reduce((s, a) => s + (a.value || 0), 0), ''],
    [''],
    ['LIABILITIES', 'Value'],
    ...liabs.map(l => [l.name, l.value || 0, '']),
    ['Total Liabilities', liabs.reduce((s, l) => s + (l.value || 0), 0), ''],
    [''],
    ['NET WORTH HISTORY', 'Month', 'Value'],
    ...history.map(h => ['', h.month, h.value]),
  ];
  downloadCSV(`finovo-networth-${new Date().toISOString().slice(0,10)}.csv`, rows);
}

function exportGoalsCSV(goalsData) {
  const goals = goalsData || [];
  const rows = [
    ['Finovo — Goals Export', new Date().toLocaleDateString()],
    [''],
    ['Goal', 'Target', 'Current', 'Progress', 'Target Date', 'Category'],
    ...goals.map(g => [
      g.name,
      g.target || 0,
      g.current || 0,
      `${Math.round(((g.current || 0) / (g.target || 1)) * 100)}%`,
      g.targetDate || '',
      g.category || '',
    ]),
  ];
  downloadCSV(`finovo-goals-${new Date().toISOString().slice(0,10)}.csv`, rows);
}

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtShort = (n) => {
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(0)}k`;
  return `$${Math.round(n)}`;
};

const CHART_COLORS = [
  '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

const PIE_COLORS = [
  '#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444',
  '#06b6d4','#ec4899','#84cc16','#f97316','#6366f1',
];

function KPI({ label, value, sub, positive, icon }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-slate-400 text-xs font-medium uppercase tracking-wider">{label}</div>
        <div className="text-slate-600">{icon}</div>
      </div>
      <div className="text-2xl font-bold text-slate-100 mb-1">{value}</div>
      {sub && (
        <div className={`text-xs flex items-center gap-1 ${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
          {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {sub}
        </div>
      )}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label, prefix = '$' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-xl text-xs">
      <div className="font-medium text-slate-300 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-400">{p.name}:</span>
          <span className="text-slate-200 font-medium">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function Reports() {
  const { budgetData, netWorthData, goalsData, userId } = useUserData();
  const [activeSection, setActiveSection] = useState('spending');
  const [moodCheckins,  setMoodCheckins]  = useState([]);

  // Load mood check-in history from Supabase
  useEffect(() => {
    if (!userId) return;
    loadMoodCheckins(userId).then(setMoodCheckins).catch(() => {});
  }, [userId]);

  const categories = budgetData?.categories || [];
  const income     = budgetData?.income     || 0;
  const history    = netWorthData?.history  || [];
  const goals      = goalsData             || [];

  // ── Derived metrics ──────────────────────────────────────────────────────────
  const totalSpent    = categories.reduce((s, c) => s + (c.spent  || 0), 0);
  const totalBudgeted = categories.reduce((s, c) => s + (c.budgeted || 0), 0);
  const monthlySavings = income - totalSpent;
  const savingsRate    = income > 0 ? Math.round((monthlySavings / income) * 100) : 0;

  // Spending categories sorted by spent
  const spendingData = [...categories]
    .filter(c => c.spent > 0)
    .sort((a, b) => b.spent - a.spent)
    .map((c, i) => ({
      name: c.name,
      Spent: c.spent,
      Budget: c.budgeted,
      fill: CHART_COLORS[i % CHART_COLORS.length],
    }));

  // Pie chart data
  const pieData = spendingData.map((c, i) => ({
    name: c.name,
    value: c.Spent,
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  // Income vs spending bar chart (simulated last 3 months using current data)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const currentMonthIdx = new Date().getMonth();
  const incomeVsSpending = months.slice(Math.max(0, currentMonthIdx - 2), currentMonthIdx + 1).map((m, i) => {
    const variance = (i - 1) * 0.05;
    return {
      month: m,
      Income: Math.round(income * (1 + variance * 0.1)),
      Spent: Math.round(totalSpent * (1 + variance * 0.15)),
    };
  });

  // Net worth trend
  const nwHistory = history.map(h => ({ month: h.month, 'Net Worth': h.value }));

  // Budget vs actual
  const budgetComparison = categories
    .filter(c => c.budgeted > 0)
    .slice(0, 8)
    .map(c => ({
      name: c.name.length > 10 ? c.name.slice(0, 10) + '…' : c.name,
      Budgeted: c.budgeted,
      Spent: c.spent || 0,
    }));

  // Goals progress
  const goalsProgress = goals.slice(0, 6).map(g => ({
    name: g.name.length > 12 ? g.name.slice(0, 12) + '…' : g.name,
    Progress: Math.min(Math.round(((g.current || 0) / (g.target || 1)) * 100), 100),
  }));

  // ── Mood chart data ────────────────────────────────────────────────────────
  const moodChartData = moodCheckins.map(c => {
    const opt = MOOD_OPTIONS.find(m => m.id === c.mood);
    return {
      weekKey:   c.week_key,
      weekLabel: c.week_key.replace(/^(\d{4})-W(\d+)$/, 'W$2'),
      score:     opt?.score ?? 3,
      mood:      c.mood,
      emoji:     opt?.emoji ?? '😐',
      label:     opt?.label ?? c.mood,
      dot:       opt?.dot ?? '#94a3b8',
      date:      new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    };
  });

  const moodCounts = MOOD_OPTIONS.reduce((acc, m) => {
    acc[m.id] = moodCheckins.filter(c => c.mood === m.id).length;
    return acc;
  }, {});

  const dominantMood = MOOD_OPTIONS.reduce((best, m) =>
    moodCounts[m.id] > (moodCounts[best?.id] || -1) ? m : best, null);

  const avgScore = moodChartData.length > 0
    ? (moodChartData.reduce((s, d) => s + d.score, 0) / moodChartData.length).toFixed(1)
    : null;

  const SECTIONS = [
    { id: 'spending',  label: 'Spending',   icon: <DollarSign size={15} /> },
    { id: 'budget',    label: 'Budget',      icon: <BarChart2 size={15} /> },
    { id: 'networth',  label: 'Net Worth',   icon: <TrendingUp size={15} /> },
    { id: 'goals',     label: 'Goals',       icon: <Target size={15} /> },
    { id: 'mood',      label: 'Mood',        icon: <Heart size={15} /> },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Reports & Insights</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} · Your financial overview
          </p>
        </div>
        {/* Export buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium mr-1">
            <Download size={13} /> Export:
          </div>
          <button
            onClick={() => exportBudgetCSV(budgetData)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-300 hover:text-slate-100 transition-all"
          >
            <FileText size={12} /> Budget CSV
          </button>
          <button
            onClick={() => exportNetWorthCSV(netWorthData)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-300 hover:text-slate-100 transition-all"
          >
            <FileText size={12} /> Net Worth CSV
          </button>
          <button
            onClick={() => exportGoalsCSV(goalsData)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-300 hover:text-slate-100 transition-all"
          >
            <FileText size={12} /> Goals CSV
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-700 hover:bg-blue-600 border border-blue-600 rounded-lg text-xs text-blue-100 hover:text-white transition-all"
          >
            <Printer size={12} /> Save PDF
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          label="Monthly Income"
          value={fmt(income)}
          icon={<DollarSign size={16} />}
          sub={income > 0 ? 'Gross monthly' : 'Set in Budget →'}
          positive
        />
        <KPI
          label="Total Spent"
          value={fmt(totalSpent)}
          icon={<BarChart2 size={16} />}
          sub={income > 0 ? `${Math.round((totalSpent / income) * 100)}% of income` : undefined}
          positive={totalSpent <= income}
        />
        <KPI
          label="Monthly Savings"
          value={fmt(monthlySavings)}
          icon={<TrendingUp size={16} />}
          sub={income > 0 ? `${savingsRate}% savings rate` : undefined}
          positive={monthlySavings >= 0}
        />
        <KPI
          label="Budget Used"
          value={totalBudgeted > 0 ? `${Math.round((totalSpent / totalBudgeted) * 100)}%` : '—'}
          icon={<PieIcon size={16} />}
          sub={totalBudgeted > 0 ? `${fmt(totalBudgeted)} total budget` : undefined}
          positive={totalSpent <= totalBudgeted}
        />
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 bg-slate-800/50 border border-slate-700 rounded-xl p-1 w-fit">
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeSection === s.id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {/* ── Spending section ── */}
      {activeSection === 'spending' && (
        <div className="space-y-6">
          {spendingData.length === 0 ? (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-10 text-center">
              <BarChart2 size={32} className="text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 font-medium">No spending data yet</p>
              <p className="text-sm text-slate-500 mt-1">Log expenses in the Budget tab to see reports here.</p>
            </div>
          ) : (
            <>
              {/* Spending bar chart */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="font-semibold text-slate-200 text-sm mb-4">Spending by Category</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={spendingData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={v => v.length > 8 ? v.slice(0, 8) + '…' : v}
                    />
                    <YAxis
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={fmtShort}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="Spent" radius={[4, 4, 0, 0]}>
                      {spendingData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Spending pie */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-200 text-sm mb-4">Spending Distribution</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Category breakdown table */}
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-200 text-sm mb-4">Category Breakdown</h3>
                  <div className="space-y-2">
                    {spendingData.slice(0, 6).map((c, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c.fill }} />
                        <span className="text-xs text-slate-400 flex-1 truncate">{c.name}</span>
                        <span className="text-xs font-medium text-slate-300">{fmt(c.Spent)}</span>
                        <span className="text-xs text-slate-500 w-8 text-right">
                          {totalSpent > 0 ? `${Math.round((c.Spent / totalSpent) * 100)}%` : '—'}
                        </span>
                      </div>
                    ))}
                    {totalSpent > 0 && (
                      <div className="pt-2 border-t border-slate-700/60 flex justify-between">
                        <span className="text-xs font-semibold text-slate-300">Total</span>
                        <span className="text-xs font-bold text-slate-200">{fmt(totalSpent)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Income vs Spending */}
              {income > 0 && incomeVsSpending.length > 0 && (
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-200 text-sm mb-1">Income vs. Spending</h3>
                  <p className="text-xs text-slate-500 mb-4">Recent months estimate</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={incomeVsSpending} barGap={4}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={fmtShort} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="Income" fill="#10b981" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Spent"  fill="#3b82f6" radius={[3, 3, 0, 0]} />
                      <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Budget section ── */}
      {activeSection === 'budget' && (
        <div className="space-y-6">
          {budgetComparison.length === 0 ? (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-10 text-center">
              <BarChart2 size={32} className="text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 font-medium">No budget categories set up</p>
              <p className="text-sm text-slate-500 mt-1">Add categories in the Budget tab to see comparisons here.</p>
            </div>
          ) : (
            <>
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="font-semibold text-slate-200 text-sm mb-1">Budget vs. Actual</h3>
                <p className="text-xs text-slate-500 mb-4">How your spending compares to your plan</p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={budgetComparison} layout="vertical" margin={{ left: 0, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={fmtShort} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} axisLine={false} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="Budgeted" fill="#334155" radius={[0, 3, 3, 0]} />
                    <Bar dataKey="Spent" fill="#3b82f6" radius={[0, 3, 3, 0]} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Over/under table */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="font-semibold text-slate-200 text-sm mb-4">Category Status</h3>
                <div className="space-y-2">
                  {categories.filter(c => c.budgeted > 0).map((c, i) => {
                    const pct = c.budgeted > 0 ? Math.round(((c.spent || 0) / c.budgeted) * 100) : 0;
                    const over = pct > 100;
                    const warn = pct >= 80 && !over;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-sm w-4">{c.icon}</span>
                        <span className="text-xs text-slate-400 flex-1 truncate">{c.name}</span>
                        <div className="w-24 bg-slate-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all ${over ? 'bg-rose-400' : warn ? 'bg-amber-400' : 'bg-blue-400'}`}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium w-10 text-right ${over ? 'text-rose-400' : warn ? 'text-amber-400' : 'text-slate-400'}`}>
                          {pct}%
                        </span>
                        <span className={`text-xs w-16 text-right ${over ? 'text-rose-400' : 'text-slate-500'}`}>
                          {over ? `+${fmt((c.spent || 0) - c.budgeted)}` : `-${fmt(c.budgeted - (c.spent || 0))}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Net Worth section ── */}
      {activeSection === 'networth' && (
        <div className="space-y-6">
          {nwHistory.length < 2 ? (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-10 text-center">
              <TrendingUp size={32} className="text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 font-medium">Not enough history yet</p>
              <p className="text-sm text-slate-500 mt-1">Add accounts in the Net Worth tab. Your trend chart will appear as months accumulate.</p>
            </div>
          ) : (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
              <h3 className="font-semibold text-slate-200 text-sm mb-1">Net Worth Trend</h3>
              <p className="text-xs text-slate-500 mb-4">Total assets minus liabilities over time</p>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={nwHistory}>
                  <defs>
                    <linearGradient id="nwGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={fmtShort} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="Net Worth" stroke="#3b82f6" fill="url(#nwGradient)" strokeWidth={2} dot={{ fill: '#3b82f6', r: 3 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Assets vs Liabilities */}
          {(netWorthData?.assets?.length > 0 || netWorthData?.liabilities?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-3">Assets</h3>
                <div className="space-y-2">
                  {(netWorthData?.assets || []).map((a, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-xs text-slate-400 truncate flex-1">{a.name}</span>
                      <span className="text-xs font-medium text-slate-200 ml-2">{fmt(a.value)}</span>
                    </div>
                  ))}
                  <div className="pt-2 border-t border-slate-700/60 flex justify-between">
                    <span className="text-xs font-semibold text-slate-300">Total</span>
                    <span className="text-xs font-bold text-emerald-400">
                      {fmt((netWorthData?.assets || []).reduce((s, a) => s + a.value, 0))}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="text-xs font-semibold text-rose-400 uppercase tracking-wider mb-3">Liabilities</h3>
                <div className="space-y-2">
                  {(netWorthData?.liabilities || []).length === 0 ? (
                    <p className="text-xs text-slate-500">No liabilities added</p>
                  ) : (
                    (netWorthData?.liabilities || []).map((l, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-xs text-slate-400 truncate flex-1">{l.name}</span>
                        <span className="text-xs font-medium text-slate-200 ml-2">{fmt(l.value)}</span>
                      </div>
                    ))
                  )}
                  {(netWorthData?.liabilities || []).length > 0 && (
                    <div className="pt-2 border-t border-slate-700/60 flex justify-between">
                      <span className="text-xs font-semibold text-slate-300">Total</span>
                      <span className="text-xs font-bold text-rose-400">
                        {fmt((netWorthData?.liabilities || []).reduce((s, l) => s + l.value, 0))}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Goals section ── */}
      {activeSection === 'goals' && (
        <div className="space-y-6">
          {goals.length === 0 ? (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-10 text-center">
              <Target size={32} className="text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 font-medium">No goals set up yet</p>
              <p className="text-sm text-slate-500 mt-1">Add goals in the Goals tab to track your progress here.</p>
            </div>
          ) : (
            <>
              {goalsProgress.length > 0 && (
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-200 text-sm mb-4">Goals Progress (%)</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={goalsProgress} layout="vertical" margin={{ left: 0, right: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} axisLine={false} unit="%" />
                      <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} axisLine={false} width={90} />
                      <Tooltip formatter={(v) => `${v}%`} contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="Progress" radius={[0, 4, 4, 0]}>
                        {goalsProgress.map((entry, i) => (
                          <Cell key={i} fill={entry.Progress >= 80 ? '#10b981' : entry.Progress >= 50 ? '#3b82f6' : '#f59e0b'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Goals cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {goals.map((g, i) => {
                  const pct = Math.min(Math.round(((g.current || 0) / (g.target || 1)) * 100), 100);
                  const remaining = (g.target || 0) - (g.current || 0);
                  return (
                    <div key={g.id || i} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">{g.icon || '🎯'}</span>
                        <span className="text-sm font-semibold text-slate-200 truncate">{g.name}</span>
                      </div>
                      <div className="flex items-end justify-between mb-2">
                        <span className="text-xs text-slate-500">{fmt(g.current || 0)} saved</span>
                        <span className="text-xs font-bold text-slate-300">{pct}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-1.5 mb-2">
                        <div
                          className={`h-1.5 rounded-full ${pct >= 80 ? 'bg-emerald-400' : pct >= 50 ? 'bg-blue-400' : 'bg-amber-400'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>Target: {fmt(g.target || 0)}</span>
                        {remaining > 0 && <span>{fmt(remaining)} to go</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Mood section ── */}
      {activeSection === 'mood' && (
        <div className="space-y-6">
          {moodCheckins.length === 0 ? (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-10 text-center">
              <Heart size={32} className="text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 font-medium">No mood check-ins yet</p>
              <p className="text-sm text-slate-500 mt-1">
                The weekly check-in appears every Monday. Your emotional journey will be tracked here over time.
              </p>
            </div>
          ) : (
            <>
              {/* KPI row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Total Check-ins', value: moodCheckins.length },
                  { label: 'Most Common',      value: dominantMood ? `${dominantMood.emoji} ${dominantMood.label}` : '—' },
                  { label: 'Avg. Score',        value: avgScore ? `${avgScore} / 5` : '—' },
                  { label: 'Latest Mood',        value: moodChartData.length > 0
                      ? `${moodChartData[moodChartData.length - 1].emoji} ${moodChartData[moodChartData.length - 1].label}`
                      : '—' },
                ].map(k => (
                  <div key={k.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{k.label}</div>
                    <div className="text-xl font-bold text-slate-100">{k.value}</div>
                  </div>
                ))}
              </div>

              {/* Line chart */}
              {moodChartData.length >= 2 && (
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-200 text-sm mb-1">Financial Mood Over Time</h3>
                  <p className="text-xs text-slate-500 mb-4">
                    1 = Overwhelmed · 2 = Stressed · 3 = Okay · 4 = Good · 5 = Great
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={moodChartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis
                        dataKey="weekLabel"
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        domain={[0.5, 5.5]}
                        ticks={[1, 2, 3, 4, 5]}
                        tickFormatter={v => (['', '😰', '😟', '😐', '😊', '🤩'])[v] || v}
                        tick={{ fill: '#94a3b8', fontSize: 13 }}
                        tickLine={false}
                        axisLine={false}
                        width={30}
                      />
                      <Tooltip
                        contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                        formatter={(_, __, props) => [`${props.payload.emoji} ${props.payload.label}`, 'Mood']}
                        labelFormatter={l => `Week ${l}`}
                        labelStyle={{ color: '#cbd5e1' }}
                      />
                      <ReferenceLine y={3} stroke="#334155" strokeDasharray="4 4" />
                      <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#3b82f6"
                        strokeWidth={2.5}
                        dot={({ cx, cy, payload }) => (
                          <circle key={payload.weekKey} cx={cx} cy={cy} r={5}
                            fill={payload.dot} stroke="#0f172a" strokeWidth={1.5} />
                        )}
                        activeDot={{ r: 7, strokeWidth: 2, stroke: '#0f172a' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Mood distribution */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="font-semibold text-slate-200 text-sm mb-4">Mood Distribution</h3>
                <div className="space-y-3">
                  {MOOD_OPTIONS.map(m => {
                    const count = moodCounts[m.id] || 0;
                    const pct2  = moodCheckins.length > 0 ? Math.round((count / moodCheckins.length) * 100) : 0;
                    return (
                      <div key={m.id} className="flex items-center gap-3">
                        <span className="text-lg leading-none w-6 text-center">{m.emoji}</span>
                        <span className="text-xs text-slate-400 w-24">{m.label}</span>
                        <div className="flex-1 bg-slate-700 rounded-full h-2 overflow-hidden">
                          <div className="h-2 rounded-full transition-all" style={{ width: `${pct2}%`, background: m.dot }} />
                        </div>
                        <span className="text-xs text-slate-500 w-16 text-right">{count}× ({pct2}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* History list */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <h3 className="font-semibold text-slate-200 text-sm mb-4">Check-in History</h3>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {[...moodChartData].reverse().map(d => (
                    <div key={d.weekKey}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-700/40 border border-slate-700/60">
                      <span className="text-xl leading-none">{d.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-200">{d.label}</p>
                        <p className="text-xs text-slate-500">{d.date} · {d.weekKey}</p>
                      </div>
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: d.dot }} />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
